let formDataCache = new WeakMap();

export const parseFormData = (app) => {
  if (!app) return {};
  if (formDataCache.has(app)) return formDataCache.get(app);

  let parsed = {};
  const raw = app.form_data;
  if (raw) {
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || typeof parsed !== 'object') parsed = {};
    } catch {
      parsed = {};
    }
  }

  formDataCache.set(app, parsed);
  return parsed;
};

export const hasAppValue = (value) => {
  if (value == null) return false;
  return String(value).trim().length > 0;
};

export const getAppField = (app, keys, fallback = 'N/A') => {
  if (!app) return fallback;
  const keyList = Array.isArray(keys) ? keys : [keys];
  const sources = [app, parseFormData(app)];

  for (const source of sources) {
    for (const key of keyList) {
      const value = source?.[key];
      if (hasAppValue(value)) return value;
    }
  }

  return fallback;
};

export const resolveApplicantEmailFromApp = (app) => {
  const raw = getAppField(app, ['email', 'emailAddress', 'applicant_email'], '');
  const email = String(raw).trim();
  if (!hasAppValue(email) || email === 'N/A') return '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
};

export const formatApplicationNo = (app) => {
  const value = app?.application_no;
  if (value == null || value === '') return '—';
  return String(value);
};

export const formatRating = (app, keys) => {
  const value = getAppField(app, keys, '');
  if (!hasAppValue(value) || value === 'N/A') return 'N/A';
  return `${value}/10`;
};

const EMPLOYMENT_ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

const isInvalidEmploymentValue = (value) => {
  if (!hasAppValue(value)) return true;
  const normalized = String(value).trim().toUpperCase();
  return normalized === 'NA' || normalized === 'N/A' || normalized === 'NONE';
};

const parseEmploymentBlocksFromText = (text) => {
  if (!hasAppValue(text)) return [];

  const entries = [];
  const blocks = String(text).split(/\n*---\s*(.*?)\s+Employment\s*---\n*/i);

  for (let index = 1; index < blocks.length; index += 2) {
    const label = blocks[index]?.trim();
    const body = blocks[index + 1] || '';
    const company = body.match(/Company\s*&\s*Role:\s*(.*?)(?:\n|$)/i)?.[1]?.trim() || '';
    const reason = body.match(/Reason\s*for\s*Leaving:\s*(.*?)(?:\n|$)/i)?.[1]?.trim() || '';
    const clearance = body.match(/Clearance\s*&\s*COE:\s*(.*?)(?:\n|$)/i)?.[1]?.trim() || '';

    if (isInvalidEmploymentValue(company)) continue;

    entries.push({
      label: label ? `${label} Employment` : `Employment ${entries.length + 1}`,
      company,
      reason: isInvalidEmploymentValue(reason) ? '' : reason,
      clearance: isInvalidEmploymentValue(clearance) ? '' : clearance,
    });
  }

  return entries;
};

export const getEmploymentHistoryEntries = (app) => {
  if (!app) return [];

  const formData = parseFormData(app);
  const structuredEntries = EMPLOYMENT_ORDINALS.map((ordinal) => {
    const company = (formData[`employmentHistory${ordinal}`] || '').trim();
    const reason = (formData[`reasonForSeparation${ordinal}`] || '').trim();
    const clearance = (formData[`clearanceAndCoe${ordinal}`] || '').trim();

    if (isInvalidEmploymentValue(company)) return null;

    return {
      label: `${ordinal} Employment`,
      company,
      reason: isInvalidEmploymentValue(reason) ? '' : reason,
      clearance: isInvalidEmploymentValue(clearance) ? '' : clearance,
    };
  }).filter(Boolean);

  if (structuredEntries.length) return structuredEntries;

  return parseEmploymentBlocksFromText(app.total_experience_desc || '');
};

export const formatApplicantLegalName = (app) => {
  const last = getAppField(app, ['last_name', 'lastName'], '');
  const first = getAppField(app, ['first_name', 'firstName'], '');
  const middle = getAppField(app, ['middle_name', 'middleName'], '');
  const suffix = getAppField(app, ['suffix'], '');

  const clean = (value) => (hasAppValue(value) && value !== 'N/A' ? String(value).trim() : '');

  const lastName = clean(last);
  const firstName = clean(first);
  const middleName = clean(middle);
  const suffixName = clean(suffix);

  if (lastName) {
    const rest = [firstName, middleName, suffixName].filter(Boolean).join(' ');
    return rest ? `${lastName}, ${rest}` : lastName;
  }

  const fallback = [firstName, middleName, suffixName].filter(Boolean).join(' ');
  if (fallback) return fallback;

  return getAppField(app, ['full_name', 'name'], 'N/A');
};

export const formatRehireEligibility = (app) => {
  const value = getAppField(app, ['rehire_eligibility', 'rehire'], '');
  if (!hasAppValue(value) || value === 'N/A') return 'N/A';

  const normalized = String(value).trim().toLowerCase();
  if (value === true || normalized === 'y' || normalized === 'yes') return 'Y';
  if (value === false || normalized === 'n' || normalized === 'no') return 'N';
  return value;
};

export const formatInterviewDate = (value, lang = 'en') => {
  if (!value || value === 'N/A') return '';

  const normalized = String(value).trim().replace(' ', 'T');
  const date = new Date(normalized);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  return String(value);
};

export const toDateTimeLocalValue = (value) => {
  if (!value || value === 'N/A') return '';

  const normalized = String(value).trim().replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const splitInterviewDateParts = (value) => {
  const localValue = toDateTimeLocalValue(value);
  if (!localValue) return { date: '', time: '' };

  const [date, time] = localValue.split('T');
  return { date: date || '', time: time || '' };
};

export const combineInterviewDateParts = (date, time) => {
  const cleanDate = String(date || '').trim();
  const cleanTime = String(time || '').trim();
  if (!cleanDate || !cleanTime) return '';
  return `${cleanDate}T${cleanTime}`;
};

export const getExperienceSummary = (app) => {
  const direct = getAppField(app, ['total_years_work', 'totalAllExperience', 'totalExperience'], '');
  if (hasAppValue(direct) && direct !== 'N/A') return direct;

  const desc = String(app?.total_experience_desc || '').trim();
  if (!desc) return '';

  const summary = desc.split(/\n*---\s*\d/i)[0].trim();
  return summary;
};
